import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Organization, OrgMember } from '@examing/types';

interface OrgState {
  currentOrg: Organization | null;
  organizations: Organization[];
  members: OrgMember[];
  isLoading: boolean;
  error: string | null;
}

interface OrgActions {
  setCurrentOrg: (org: Organization | null) => void;
  setOrganizations: (orgs: Organization[]) => void;
  switchOrg: (orgId: string) => void;
  fetchOrganizations: () => Promise<void>;
  fetchMembers: () => Promise<void>;
  createOrganization: (data: { name: string; slug?: string }) => Promise<Organization>;
  updateOrganization: (data: Partial<Organization>) => Promise<void>;
}

type OrgStore = OrgState & OrgActions;

export const useOrgStore = create<OrgStore>()(
  persist(
    (set, get) => ({
      currentOrg: null,
      organizations: [],
      members: [],
      isLoading: false,
      error: null,

      setCurrentOrg: (org) => set({ currentOrg: org }),
      setOrganizations: (orgs) => set({ organizations: orgs }),

      switchOrg: (orgId) => {
        const org = get().organizations.find((o) => o.id === orgId);
        if (org) {
          set({ currentOrg: org, members: [] });
          get().fetchMembers();
        }
      },

      fetchOrganizations: async () => {
        set({ isLoading: true });
        try {
          const response = await fetch('/api/organizations');
          if (!response.ok) throw new Error('Failed to fetch organizations');
          const orgs = await response.json();
          set({ organizations: orgs, isLoading: false });
          if (!get().currentOrg && orgs.length > 0) {
            set({ currentOrg: orgs[0] });
          }
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to fetch', isLoading: false });
        }
      },

      fetchMembers: async () => {
        const org = get().currentOrg;
        if (!org) return;
        try {
          const response = await fetch(`/api/organizations/${org.id}/members`);
          if (!response.ok) throw new Error('Failed to fetch members');
          const result = await response.json();
          // Support both paginated response { data, meta } and plain array
          const members = Array.isArray(result) ? result : (result.data || []);
          set({ members });
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to fetch members' });
        }
      },

      createOrganization: async (data) => {
        set({ isLoading: true });
        try {
          const response = await fetch('/api/organizations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
          });
          if (!response.ok) throw new Error('Failed to create organization');
          const org = await response.json();
          set((state) => ({
            organizations: [...state.organizations, org],
            currentOrg: org,
            isLoading: false,
          }));
          return org;
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to create', isLoading: false });
          throw error;
        }
      },

      updateOrganization: async (data) => {
        const org = get().currentOrg;
        if (!org) throw new Error('No organization selected');
        try {
          const response = await fetch(`/api/organizations/${org.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
          });
          if (!response.ok) throw new Error('Failed to update organization');
          const updated = await response.json();
          set((state) => ({
            currentOrg: updated,
            organizations: state.organizations.map((o) => (o.id === updated.id ? updated : o)),
          }));
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to update' });
          throw error;
        }
      },
    }),
    {
      name: 'examing-org',
      partialize: (state) => ({
        currentOrg: state.currentOrg,
      }),
    }
  )
);
